/**
 * Error classification for the knowledge explorer.
 *
 * `apiClient.request()` collapses every failure into `new Error(body.error ||
 * 'HTTP <status>')`, so the status code is only recoverable from the message.
 * This module turns that string back into one of the states spec §11 requires
 * every view to be able to render, so panels never have to string-match
 * themselves.
 */

/** @typedef {'error'|'offline'|'permission'|'session'|'noSource'} ErrorKind */

export const ERROR_KINDS = {
  error: 'error',
  offline: 'offline',
  permission: 'permission',
  session: 'session',
  noSource: 'noSource'
};

const SESSION_PATTERNS = [/HTTP 401/i, /unauthori[sz]ed/i, /session (expired|not found)/i, /no active session/i, /^no session$/i];
const PERMISSION_PATTERNS = [/HTTP 40[37]/i, /forbidden/i, /access denied/i, /app_not_licensed/i, /tenant_suspended/i];
const OFFLINE_PATTERNS = [/failed to fetch/i, /network ?error/i, /request timed out/i, /load failed/i];

/**
 * @param {unknown} error
 * @returns {ErrorKind}
 */
export function classifyError(error) {
  if (!error) return ERROR_KINDS.error;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return ERROR_KINDS.offline;

  const message = typeof error === 'string' ? error : error.message || '';
  if (SESSION_PATTERNS.some((pattern) => pattern.test(message))) return ERROR_KINDS.session;
  if (PERMISSION_PATTERNS.some((pattern) => pattern.test(message))) return ERROR_KINDS.permission;
  if (OFFLINE_PATTERNS.some((pattern) => pattern.test(message))) return ERROR_KINDS.offline;
  return ERROR_KINDS.error;
}

/**
 * Technical detail worth showing under a friendly headline. Spec §10.1 forbids
 * raw technical payloads as the *primary* message, so callers render this as
 * secondary copy only, and never for the states that have their own wording.
 *
 * @param {unknown} error
 * @returns {string|null}
 */
export function errorDetail(error) {
  if (!error) return null;
  const message = typeof error === 'string' ? error : error.message || '';
  if (!message || /^HTTP \d+$/i.test(message)) return null;
  return message;
}
